import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8c7177fb-7a38-4283-a68a-657840201e44/artifacts/mq62gpje_Vainavi-Educational-Institutions_Final_1.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Courses", path: "/courses" },
  { name: "Gallery", path: "/gallery" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img
              src={LOGO_URL}
              alt="Vainavi Educational Institutions"
              className="h-20 w-auto object-contain"
              data-testid="nav-logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`font-body text-sm font-semibold uppercase tracking-widest transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-crimson border-b-2 border-crimson pb-1"
                    : "text-navy hover:text-crimson"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-apply-now-btn"
              className="bg-crimson text-white px-6 py-2.5 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Apply Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-navy hover:text-crimson transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          data-testid="mobile-menu"
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? "max-h-96 pb-6" : "max-h-0"
          }`}
        >
          <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                className={`py-3 px-4 font-body text-sm font-semibold uppercase tracking-widest transition-colors ${
                  location.pathname === link.path
                    ? "text-crimson bg-red-50"
                    : "text-navy hover:text-crimson hover:bg-slate-50"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-apply-now-btn"
              className="mt-2 mx-4 bg-crimson text-white px-6 py-3 font-body text-sm font-semibold uppercase tracking-wider text-center hover:bg-crimson-hover transition-all duration-300"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
